import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, mediaUrl } from "@/lib/api";
import { PHILOSOPHY_FALLBACK } from "@/pages/Home";

const label = "font-mono-label text-[10px] uppercase tracking-luxe text-white/40 mb-2 block";
const field = "w-full bg-transparent border border-white/20 px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-white/60 text-sm";
const btn = "px-10 py-4 bg-white text-black font-mono-label text-xs uppercase tracking-luxe hover-invert hover:bg-transparent hover:text-white border border-white disabled:opacity-50";

// ---------- Shipping ----------
export const ShippingPanel = () => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/shipping").then((r) => setCfg(r.data)).catch(() => setCfg({}));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/shipping", cfg);
      toast.success("Shipping saved");
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (!cfg) return <p className="text-white/40 font-mono-label text-sm">Loading…</p>;

  return (
    <div data-testid="shipping-panel" className="space-y-6 max-w-2xl">
      <p className="text-white/50 text-sm">Shipping is within Switzerland only. Set the fee and free-shipping threshold in CHF; customers paying in EUR see both converted at the daily rate (rounded up to whole euros).</p>
      {Object.entries(cfg).map(([cur, c]) => (
        <div key={cur} className="border border-white/10 p-6">
          <p className="font-mono-label text-xs uppercase tracking-luxe text-white/70 mb-4">{cur.toUpperCase()} · {c.label}</p>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className={label}>Region label</label>
              <input data-testid={`ship-label-${cur}`} value={c.label || ""} onChange={(e) => setCfg({ ...cfg, [cur]: { ...c, label: e.target.value } })} className={field} />
            </div>
            <div>
              <label className={label}>Shipping fee ({cur.toUpperCase()})</label>
              <input data-testid={`ship-fee-${cur}`} type="number" value={c.fee ?? 0} onChange={(e) => setCfg({ ...cfg, [cur]: { ...c, fee: parseFloat(e.target.value || 0) } })} className={field} />
            </div>
            <div>
              <label className={label}>Free over ({cur.toUpperCase()})</label>
              <input data-testid={`ship-free-${cur}`} type="number" value={c.free_over ?? 0} onChange={(e) => setCfg({ ...cfg, [cur]: { ...c, free_over: parseFloat(e.target.value || 0) } })} className={field} />
            </div>
          </div>
        </div>
      ))}
      <button data-testid="shipping-save" onClick={save} disabled={saving} className={btn}>{saving ? "Saving…" : "Save Shipping"}</button>
    </div>
  );
};

// ---------- Homepage content ----------
export const ContentPanel = () => {
  const [hp, setHp] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/config").then((r) => setHp(r.data.homepage)).catch(() => setHp({}));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/homepage", hp);
      toast.success("Homepage content saved");
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (!hp) return <p className="text-white/40 font-mono-label text-sm">Loading…</p>;

  const fields = [
    ["marquee", "Scrolling marquee text", "input"],
    ["latest_eyebrow", "New Arrivals — eyebrow label", "input"],
    ["latest_title", "New Arrivals — heading", "input"],
    ["philosophy_label", "Philosophy — section name (shown after “02 —”)", "input"],
    ["philosophy_title", "Philosophy — heading", "input"],
    ["philosophy_text", "Philosophy — paragraph", "textarea"],
    ["instagram_url", "Instagram profile URL (leave empty to hide)", "input"],
    ["tiktok_url", "TikTok profile URL (leave empty to hide)", "input"],
    ["footer_heading", "Footer Brand Heading", "input"],
    ["footer_description", "Footer Brand Description", "textarea"],
  ];

  return (
    <div data-testid="content-panel" className="space-y-5 max-w-2xl">
      <p className="text-white/50 text-sm">Edit homepage copy without touching code. The hero video/title lives in the Hero Video tab. Choose which products appear under “New Arrivals” by marking them <em>Featured</em> in the product editor.</p>
      {fields.map(([k, l, type]) => (
        <div key={k}>
          <label className={label}>{l}</label>
          {type === "textarea"
            ? <textarea data-testid={`hp-${k}`} rows={3} value={hp[k] || ""} onChange={(e) => setHp({ ...hp, [k]: e.target.value })} className={field} />
            : <input data-testid={`hp-${k}`} value={hp[k] || ""} onChange={(e) => setHp({ ...hp, [k]: e.target.value })} className={field} />}
        </div>
      ))}
      <PhilosophyImage hp={hp} setHp={setHp} />
      <button data-testid="content-save" onClick={save} disabled={saving} className={btn}>{saving ? "Saving…" : "Save Content"}</button>
    </div>
  );
};

const PhilosophyImage = ({ hp, setHp }) => {
  const [busy, setBusy] = useState(false);
  const persist = async (next, msg) => {
    await api.put("/admin/homepage", next);
    setHp(next);
    toast.success(msg);
  };
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await persist({ ...hp, philosophy_image: data.path }, "Philosophy image updated");
    } catch (err) { toast.error("Upload failed"); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try { await persist({ ...hp, philosophy_image: null }, "Custom image removed — default restored"); }
    catch (err) { toast.error("Remove failed"); }
    finally { setBusy(false); }
  };
  const custom = !!hp.philosophy_image;
  return (
    <div data-testid="philosophy-image-control">
      <label className={label}>Philosophy — editorial image</label>
      <div className="flex gap-5 items-start">
        <img data-testid="philosophy-image-preview" src={custom ? mediaUrl(hp.philosophy_image) : PHILOSOPHY_FALLBACK} alt="Philosophy preview" className="w-28 aspect-[4/5] object-cover border border-white/10" />
        <div className="space-y-3">
          <p data-testid="philosophy-image-status" className="text-white/50 text-xs">{custom ? "Custom image in use" : "Default image in use"}</p>
          <label className="inline-block border border-white/30 px-5 py-3 font-mono-label text-[10px] uppercase tracking-luxe cursor-pointer hover:border-white">
            {busy ? "Working…" : custom ? "Replace Image" : "Upload Image"}
            <input data-testid="philosophy-image-upload" type="file" accept="image/*" onChange={upload} disabled={busy} className="hidden" />
          </label>
          {custom && (
            <button data-testid="philosophy-image-remove" onClick={remove} disabled={busy} className="block text-white/50 hover:text-white font-mono-label text-[10px] uppercase tracking-luxe underline">Remove — use default</button>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------- Discounts ----------
export const DiscountsPanel = () => {
  const [codes, setCodes] = useState([]);
  const [form, setForm] = useState({ code: "", type: "percent", value: "", min_subtotal: "", expires_at: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/discounts").then((r) => setCodes(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.code || form.value === "") { toast.error("Code and value required"); return; }
    setSaving(true);
    try {
      await api.post("/admin/discounts", {
        code: form.code, type: form.type, value: parseFloat(form.value),
        min_subtotal: parseFloat(form.min_subtotal || 0),
        expires_at: form.expires_at || null, active: true,
      });
      toast.success("Discount created");
      setForm({ code: "", type: "percent", value: "", min_subtotal: "", expires_at: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/admin/discounts/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="discounts-panel" className="space-y-8 max-w-2xl">
      <p className="text-white/50 text-sm">Percent codes apply to any currency. Fixed-amount codes are set in CHF and convert automatically for EUR (rounded up to whole euros). Codes apply before shipping.</p>
      <div className="border border-white/10 p-6 grid grid-cols-2 gap-4">
        <div>
          <label className={label}>Code</label>
          <input data-testid="disc-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className={field} placeholder="WELCOME10" />
        </div>
        <div>
          <label className={label}>Type</label>
          <select data-testid="disc-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={field}>
            <option value="percent" className="bg-black">Percent (%)</option>
            <option value="fixed" className="bg-black">Fixed (CHF)</option>
          </select>
        </div>
        <div>
          <label className={label}>{form.type === "percent" ? "Percent off" : "Amount off (CHF)"}</label>
          <input data-testid="disc-value" type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className={field} />
        </div>
        <div>
          <label className={label}>Min order (CHF, optional)</label>
          <input data-testid="disc-min" type="number" value={form.min_subtotal} onChange={(e) => setForm({ ...form, min_subtotal: e.target.value })} className={field} />
        </div>
        <div className="col-span-2">
          <button data-testid="disc-create" onClick={create} disabled={saving} className={btn}>{saving ? "Creating…" : "Create Code"}</button>
        </div>
      </div>
      <div className="border border-white/10 divide-y divide-white/10">
        {codes.length === 0 && <p className="p-8 text-center text-white/40 font-mono-label text-sm uppercase tracking-luxe">No codes yet</p>}
        {codes.map((c) => (
          <div key={c.id} data-testid={`disc-row-${c.code}`} className="flex items-center gap-4 p-4">
            <span className="font-mono-label text-white">{c.code}</span>
            <span className="text-xs text-white/50">{c.type === "percent" ? `${c.value}% off` : `Fr. ${c.value} off`}{c.min_subtotal ? ` · min Fr. ${c.min_subtotal}` : ""}</span>
            <button data-testid={`disc-delete-${c.code}`} onClick={() => remove(c.id)} className="ml-auto text-white/40 hover:text-red-400 font-mono-label text-xs uppercase tracking-luxe">Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- Legal ----------
const LEGAL = [
  ["terms", "Terms & Conditions"],
  ["privacy", "Privacy Policy"],
  ["shipping", "Shipping Policy"],
  ["returns", "Returns & Refunds"],
  ["imprint", "Imprint / Legal Notice"],
];

export const LegalPanel = () => {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/legal").then((r) => setContent(r.data || {})).catch(() => setContent({}));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/legal", content);
      toast.success("Legal pages saved");
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (!content) return <p className="text-white/40 font-mono-label text-sm">Loading…</p>;

  return (
    <div data-testid="legal-panel" className="space-y-5 max-w-2xl">
      <p className="text-white/50 text-sm">Paste your real legal text here. These are blank placeholders — no legal language is invented for you. Empty pages show a “not published yet” notice on the site.</p>
      {LEGAL.map(([k, l]) => (
        <div key={k}>
          <label className={label}>{l}</label>
          <textarea data-testid={`legal-${k}`} rows={4} value={content[k] || ""} onChange={(e) => setContent({ ...content, [k]: e.target.value })} className={field} placeholder={`Enter your ${l} here…`} />
        </div>
      ))}
      <button data-testid="legal-save" onClick={save} disabled={saving} className={btn}>{saving ? "Saving…" : "Save Legal Pages"}</button>
    </div>
  );
};
